import React from 'react';
import { Button, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { HistoryOrderEntity } from 'types/dto/order';
import './OrderStatusCell.scss';
import OrderStatusTag from 'components/orderStatusTag/OrderStatusTag';
import { DATE_WITH_DAY_MM_DD_FORMAT } from 'constants/consts';

interface OrderStatusCellProps {
  order: HistoryOrderEntity;
  onViewDetails?: (order: HistoryOrderEntity) => void;
}

const OrderStatusCell: React.FC<OrderStatusCellProps> = ({
  order,
  onViewDetails,
}) => {
  const { t } = useTranslation('landing');

  // Format date
  const formatDate = (dateString: string) => {
    return dayjs(dateString).format(DATE_WITH_DAY_MM_DD_FORMAT);
  };

  // Format time from ISO string
  const formatTime = (dateString?: string) => {
    if (!dateString) return '';
    return dayjs(dateString).format('HH:mm');
  };

  // Format date with time range
  const formatDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate) return '';
    const date = formatDate(startDate);
    const startTime = formatTime(startDate);
    const endTime = formatTime(endDate);
    if (!endTime) return `${date}, ${startTime}`;
    return `${date}, ${startTime} - ${endTime}`;
  };

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(order);
    }
  };

  // Render detail row
  const renderDetailRow = (label: string, value: string) => (
    <Row gutter={[8, 2]}>
      <Col xs={24} sm={8}>
        <span className="order-status-cell-detail-label">{label}</span>
      </Col>
      <Col xs={24} sm={16}>
        <span>{value}</span>
      </Col>
    </Row>
  );

  return (
    <div className="order-status-cell">
      <Row className="order-status-cell-header">
        <Col xs={24} sm={{ flex: 'none' }}>
          <div className="order-status-cell-title">
            {t('orderStatusCell.title')}
          </div>
        </Col>
        <Col xs={24} sm={{ flex: 'none' }}>
          <OrderStatusTag status={order.oph_sts} statusName={order.sts_nam} />
        </Col>
      </Row>

      <span className="order-status-cell-count">
        {t('orderStatusCell.itemsInTotal', { count: 5 })}
      </span>

      <div className="order-status-cell-details">
        {renderDetailRow(
          t('orderStatusCell.collectionDate'),
          formatDateRange(order.oph_colstrdtm, order.oph_colenddtm),
        )}
        {renderDetailRow(
          t('orderStatusCell.deliveryDate'),
          formatDateRange(order.oph_delstrdtm, order.oph_delenddtm),
        )}
        {renderDetailRow(
          t('orderStatusCell.remarks'),
          order.oph_rmktocus || '',
        )}
      </div>
      <div>
        <Button
          type="link"
          className="order-status-cell-view-button"
          onClick={handleViewDetails}
        >
          {t('viewDetails')}
        </Button>
      </div>
    </div>
  );
};

export default OrderStatusCell;
